import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase-auth';
import { useAuth } from '../../contexts/AuthContextLazy';
import { AddPartyModal } from './AddPartyModal';
import { colors } from '../../lib/colors';
import toast from 'react-hot-toast';
import {
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
  UserGroupIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  CheckCircleIcon,
  ClipboardDocumentListIcon,
  FolderIcon,
  UserIcon,
  BuildingOfficeIcon,
  CalendarIcon,
  ListBulletIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';

// Types
interface Party {
  id: string;
  name: string;
  type: 'individual' | 'organization';
  primary_email: string | null;
  status: string;
}

interface ProjectTemplate {
  id: string;
  name: string;
  code: string;
  description: string | null;
  project_type: string;
  default_fee: number | null;
  default_deposit_percentage: number | null;
  default_duration_days: number | null;
  phases: Array<{ number: number; name: string }>;
  tasks_template: Array<{
    phase: number;
    title: string;
    owner_type: string;
    category: string;
    sort_order: number;
  }>;
  documents_template: Array<{
    doc_type: string;
    label: string;
    direction: string;
    is_required: boolean;
  }>;
  payments_template: Array<{
    type: string;
    percentage: number;
    description: string;
  }>;
}

interface WizardFormData {
  // Step 1: Client Selection
  partyId: string;

  // Step 2: Type Selection
  templateId: string;
  name: string;
  description: string;

  // Step 3: Fees
  feeTotal: number;
  feeDeposit: number;
  targetCompletionDate: string;
}

const STEPS = [
  { id: 1, name: 'Client', icon: UserGroupIcon },
  { id: 2, name: 'Type', icon: ClipboardDocumentListIcon },
  { id: 3, name: 'Fees', icon: CurrencyDollarIcon },
  { id: 4, name: 'Review', icon: CheckCircleIcon },
];

export const CreateEngagementWizard: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { organization, userProfile } = useAuth();

  // State
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Data
  const [parties, setParties] = useState<Party[]>([]);
  const [templates, setTemplates] = useState<ProjectTemplate[]>([]);
  const [showAddPartyModal, setShowAddPartyModal] = useState(false);

  // Form data
  const [formData, setFormData] = useState<WizardFormData>({
    partyId: searchParams.get('partyId') || '',
    templateId: '',
    name: '',
    description: '',
    feeTotal: 0,
    feeDeposit: 0,
    targetCompletionDate: '',
  });

  // Selected items for display
  const selectedParty = parties.find(p => p.id === formData.partyId);
  const selectedTemplate = templates.find(t => t.id === formData.templateId);

  // Fetch parties
  const fetchParties = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('parties')
        .select('id, name, type, primary_email, status')
        .eq('status', 'active')
        .order('name');

      if (error) throw error;
      setParties(data || []);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error fetching parties:', error);
      }
      toast.error('Failed to load clients');
    }
  }, []);

  // Fetch templates
  const fetchTemplates = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('project_templates')
        .select('*')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      setTemplates(data || []);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error fetching templates:', error);
      }
      toast.error('Failed to load templates');
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    setLoading(true);
    Promise.all([fetchParties(), fetchTemplates()]).finally(() => {
      setLoading(false);
    });
  }, [fetchParties, fetchTemplates]);

  // Update form when template selected
  useEffect(() => {
    if (selectedTemplate) {
      const fee = selectedTemplate.default_fee || 0;
      const depositPct = selectedTemplate.default_deposit_percentage || 50;
      const durationDays = selectedTemplate.default_duration_days || 90;

      // Calculate target completion date
      const targetDate = new Date();
      targetDate.setDate(targetDate.getDate() + durationDays);

      setFormData(prev => ({
        ...prev,
        name: selectedParty
          ? `${selectedTemplate.name} - ${selectedParty.name}`
          : selectedTemplate.name,
        description: selectedTemplate.description || '',
        feeTotal: fee,
        feeDeposit: Math.round(fee * depositPct / 100),
        targetCompletionDate: targetDate.toISOString().split('T')[0],
      }));
    }
  }, [selectedTemplate, selectedParty]);

  // Step validation
  const validateStep = (step: number): boolean => {
    switch (step) {
      case 1:
        return !!formData.partyId;
      case 2:
        return !!formData.templateId && !!formData.name.trim();
      case 3:
        return formData.feeTotal >= 0;
      case 4:
        return true;
      default:
        return false;
    }
  };

  // Navigation
  const handleNext = () => {
    if (validateStep(currentStep) && currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCancel = () => {
    navigate('/portal/engagements');
  };

  // Create engagement
  const handleCreate = async () => {
    if (!organization || !userProfile || !selectedTemplate) return;

    setSubmitting(true);

    try {
      // 1. Create the project
      const { data: projectData, error: projectError } = await supabase
        .from('projects')
        .insert({
          organization_id: organization.id,
          name: formData.name,
          description: formData.description,
          status: 'engaged',
          project_type: selectedTemplate.project_type,
          template_id: selectedTemplate.id,
          fee_total: formData.feeTotal,
          fee_deposit: formData.feeDeposit,
          fee_paid: 0,
          current_phase: 1,
          total_phases: selectedTemplate.phases.length || 1,
          engaged_date: new Date().toISOString().split('T')[0],
          target_completion_date: formData.targetCompletionDate || null,
          tasks_total: selectedTemplate.tasks_template.length,
          tasks_completed: 0,
          docs_required: selectedTemplate.documents_template.filter(d => d.is_required).length,
          docs_received: 0,
          assigned_to: userProfile.id,
        });

      if (projectError) throw projectError;

      // Query for the newly created project (minimal client doesn't return inserted data)
      const { data: createdProjects, error: fetchError } = await supabase
        .from('projects')
        .select('id')
        .eq('organization_id', organization.id)
        .eq('name', formData.name)
        .order('created_at', { ascending: false })
        .limit(1);

      if (fetchError) throw fetchError;

      const project = createdProjects?.[0];
      if (!project?.id) throw new Error('Failed to create project - could not find created project');

      // 2. Create project_parties link
      const { error: partyError } = await supabase
        .from('project_parties')
        .insert({
          project_id: project.id,
          party_id: formData.partyId,
          role: 'client',
          is_primary: true,
        });

      if (partyError) throw partyError;

      // 3. Create tasks from template
      if (selectedTemplate.tasks_template.length > 0) {
        const tasksToInsert = selectedTemplate.tasks_template.map((task, index) => ({
          project_id: project.id,
          title: task.title,
          phase: task.phase,
          sort_order: task.sort_order || index,
          owner_type: task.owner_type,
          category: task.category,
          status: 'not_started',
          template_task_id: `${selectedTemplate.code}-task-${index}`,
        }));

        const { error: tasksError } = await supabase
          .from('tasks')
          .insert(tasksToInsert);

        if (tasksError) throw tasksError;
      }

      // 4. Document requirements are tracked via project.docs_required field
      // Actual project_documents records are created when documents are uploaded
      // (project_documents table requires linking to actual client_documents or compliance_documents)

      // 5. Create payments from template
      if (selectedTemplate.payments_template.length > 0 && formData.feeTotal > 0) {
        const paymentsToInsert = selectedTemplate.payments_template.map((payment, index) => ({
          project_id: project.id,
          payment_type: payment.type,
          description: payment.description,
          amount: Math.round(formData.feeTotal * payment.percentage / 100),
          status: 'pending',
        }));

        const { error: paymentsError } = await supabase
          .from('payments')
          .insert(paymentsToInsert);

        if (paymentsError) throw paymentsError;
      }

      // 6. Add current user as project member (advisor)
      const { error: memberError } = await supabase
        .from('project_members')
        .insert({
          project_id: project.id,
          platform_user_id: userProfile.id,
          member_role: 'advisor',
          can_view: true,
          can_edit: true,
          can_upload: true,
          can_download: true,
          can_view_fees: true,
          can_view_internal_notes: true,
          can_manage_members: true,
          added_by: userProfile.id,
        });

      if (memberError) throw memberError;

      toast.success('Engagement created successfully!');
      navigate(`/portal/projects/${project.id}`);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error creating engagement:', error);
      }
      toast.error('Failed to create engagement. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle new client created
  const handleClientCreated = () => {
    fetchParties();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={handleCancel}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">New Engagement</h1>
                <p className="text-sm text-gray-500">Create a new client engagement</p>
              </div>
            </div>

            {/* Step Progress */}
            <div className="hidden md:flex items-center gap-2">
              {STEPS.map((step, index) => (
                <React.Fragment key={step.id}>
                  <div
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      currentStep === step.id
                        ? 'bg-blue-100 text-blue-700'
                        : currentStep > step.id
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {currentStep > step.id ? (
                      <CheckCircleSolidIcon className="h-4 w-4" />
                    ) : (
                      <step.icon className="h-4 w-4" />
                    )}
                    <span className="hidden lg:inline">{step.name}</span>
                  </div>
                  {index < STEPS.length - 1 && (
                    <div className={`w-8 h-0.5 ${currentStep > step.id ? 'bg-green-300' : 'bg-gray-200'}`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Step Content */}
          <div className="p-6 md:p-8 min-h-[400px]">
            {/* Step 1: Client Selection */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-1">Select Client</h2>
                  <p className="text-sm text-gray-500">Choose an existing client or create a new one</p>
                </div>

                {/* Search/Filter */}
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search clients..."
                    className="w-full px-4 py-3 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Client List */}
                <div className="grid gap-3 max-h-[400px] overflow-y-auto pr-2">
                  {parties.map((party) => (
                    <button
                      key={party.id}
                      onClick={() => setFormData({ ...formData, partyId: party.id })}
                      className={`flex items-center gap-4 p-4 rounded-lg border-2 transition-all text-left ${
                        formData.partyId === party.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        formData.partyId === party.id ? 'bg-blue-100' : 'bg-gray-100'
                      }`}>
                        {party.type === 'individual' ? (
                          <UserIcon className={`h-5 w-5 ${formData.partyId === party.id ? 'text-blue-600' : 'text-gray-500'}`} />
                        ) : (
                          <BuildingOfficeIcon className={`h-5 w-5 ${formData.partyId === party.id ? 'text-blue-600' : 'text-gray-500'}`} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{party.name}</p>
                        <p className="text-sm text-gray-500 truncate">
                          {party.type === 'individual' ? 'Individual' : 'Organization'}
                          {party.primary_email && ` - ${party.primary_email}`}
                        </p>
                      </div>
                      {formData.partyId === party.id && (
                        <CheckCircleSolidIcon className="h-6 w-6 text-blue-500 flex-shrink-0" />
                      )}
                    </button>
                  ))}
                </div>

                {/* Add New Client */}
                <button
                  onClick={() => setShowAddPartyModal(true)}
                  className="w-full flex items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                >
                  <PlusIcon className="h-5 w-5" />
                  <span className="font-medium">Add New Client</span>
                </button>
              </div>
            )}

            {/* Step 2: Type Selection */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-1">Engagement Type</h2>
                  <p className="text-sm text-gray-500">Select the type of engagement and customize details</p>
                </div>

                {/* Template Selection */}
                <div className="grid gap-4 md:grid-cols-2">
                  {templates.map((template) => (
                    <button
                      key={template.id}
                      onClick={() => setFormData({ ...formData, templateId: template.id })}
                      className={`p-4 rounded-lg border-2 text-left transition-all ${
                        formData.templateId === template.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <FolderIcon className={`h-6 w-6 ${
                          formData.templateId === template.id ? 'text-blue-600' : 'text-gray-400'
                        }`} />
                        {formData.templateId === template.id && (
                          <CheckCircleSolidIcon className="h-5 w-5 text-blue-500" />
                        )}
                      </div>
                      <h3 className="font-semibold text-gray-900 mb-1">{template.name}</h3>
                      {template.description && (
                        <p className="text-sm text-gray-500 mb-3 line-clamp-2">{template.description}</p>
                      )}
                      <div className="flex flex-wrap gap-2 text-xs">
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded">
                          {template.tasks_template.length} tasks
                        </span>
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded">
                          {template.documents_template.length} docs
                        </span>
                        {template.default_duration_days && (
                          <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded">
                            ~{template.default_duration_days} days
                          </span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>

                {templates.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <ClipboardDocumentListIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p>No templates available. Please create a template first.</p>
                  </div>
                )}

                {/* Project Name */}
                {formData.templateId && (
                  <div className="pt-4 border-t border-gray-200">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Engagement Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter engagement name"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Fees */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-1">Fees & Timeline</h2>
                  <p className="text-sm text-gray-500">Set the engagement fees and target completion date</p>
                </div>

                <div className="grid gap-6 md:grid-cols-2">
                  {/* Total Fee */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Total Fee (CAD)
                    </label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                      <input
                        type="number"
                        min="0"
                        step="100"
                        value={formData.feeTotal}
                        onChange={(e) => {
                          const total = parseFloat(e.target.value) || 0;
                          const depositPct = selectedTemplate?.default_deposit_percentage || 50;
                          setFormData({
                            ...formData,
                            feeTotal: total,
                            feeDeposit: Math.round(total * depositPct / 100),
                          });
                        }}
                        className="w-full pl-8 pr-4 py-3 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  {/* Deposit */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Deposit Amount (CAD)
                    </label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                      <input
                        type="number"
                        min="0"
                        step="100"
                        value={formData.feeDeposit}
                        onChange={(e) => setFormData({ ...formData, feeDeposit: parseFloat(e.target.value) || 0 })}
                        className="w-full pl-8 pr-4 py-3 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      {formData.feeTotal > 0
                        ? `${Math.round(formData.feeDeposit / formData.feeTotal * 100)}% of total`
                        : 'Set total fee first'}
                    </p>
                  </div>

                  {/* Target Completion Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Target Completion Date
                    </label>
                    <input
                      type="date"
                      value={formData.targetCompletionDate}
                      onChange={(e) => setFormData({ ...formData, targetCompletionDate: e.target.value })}
                      className="w-full px-4 py-3 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                {/* Payment Schedule Preview */}
                {selectedTemplate && selectedTemplate.payments_template.length > 0 && formData.feeTotal > 0 && (
                  <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                    <h3 className="text-sm font-medium text-gray-700 mb-3">Payment Schedule</h3>
                    <div className="space-y-2">
                      {selectedTemplate.payments_template.map((payment, index) => (
                        <div key={index} className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">
                            {payment.type.charAt(0).toUpperCase() + payment.type.slice(1)}: {payment.description}
                          </span>
                          <span className="font-medium text-gray-900">
                            ${Math.round(formData.feeTotal * payment.percentage / 100).toLocaleString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 4: Review */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-1">Review & Create</h2>
                  <p className="text-sm text-gray-500">Review the engagement details before creating</p>
                </div>

                {/* Summary Cards */}
                <div className="grid gap-4 md:grid-cols-2">
                  {/* Client */}
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-500 mb-2">
                      <UserGroupIcon className="h-4 w-4" />
                      Client
                    </div>
                    <p className="text-lg font-semibold text-gray-900">{selectedParty?.name}</p>
                    <p className="text-sm text-gray-500">
                      {selectedParty?.type === 'individual' ? 'Individual' : 'Organization'}
                    </p>
                  </div>

                  {/* Template */}
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-500 mb-2">
                      <ClipboardDocumentListIcon className="h-4 w-4" />
                      Engagement Type
                    </div>
                    <p className="text-lg font-semibold text-gray-900">{selectedTemplate?.name}</p>
                    <p className="text-sm text-gray-500">{selectedTemplate?.code}</p>
                  </div>

                  {/* Fees */}
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-500 mb-2">
                      <CurrencyDollarIcon className="h-4 w-4" />
                      Fees
                    </div>
                    <p className="text-lg font-semibold text-gray-900">
                      ${formData.feeTotal.toLocaleString()} CAD
                    </p>
                    <p className="text-sm text-gray-500">
                      Deposit: ${formData.feeDeposit.toLocaleString()}
                    </p>
                  </div>

                  {/* Timeline */}
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-500 mb-2">
                      <CalendarIcon className="h-4 w-4" />
                      Timeline
                    </div>
                    <p className="text-lg font-semibold text-gray-900">
                      {formData.targetCompletionDate
                        ? new Date(formData.targetCompletionDate).toLocaleDateString('en-US', {
                            month: 'long',
                            day: 'numeric',
                            year: 'numeric'
                          })
                        : 'Not set'}
                    </p>
                    <p className="text-sm text-gray-500">Target completion</p>
                  </div>
                </div>

                {/* Name */}
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-2 text-sm font-medium text-blue-600 mb-2">
                    <FolderIcon className="h-4 w-4" />
                    Engagement Name
                  </div>
                  <p className="text-lg font-semibold text-gray-900">{formData.name}</p>
                  {formData.description && (
                    <p className="text-sm text-gray-600 mt-1">{formData.description}</p>
                  )}
                </div>

                {/* What will be created */}
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <h3 className="font-medium text-amber-800 mb-2">What will be created:</h3>
                  <ul className="text-sm text-amber-700 space-y-1">
                    <li className="flex items-center gap-2">
                      <CheckCircleSolidIcon className="h-4 w-4" />
                      Project with auto-generated ID (PCM-{new Date().getFullYear()}-XXX)
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircleSolidIcon className="h-4 w-4" />
                      {selectedTemplate?.tasks_template.length || 0} tasks from template
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircleSolidIcon className="h-4 w-4" />
                      {selectedTemplate?.documents_template.length || 0} document requirements
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircleSolidIcon className="h-4 w-4" />
                      {selectedTemplate?.payments_template.length || 0} payment milestones
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 md:px-8 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
            <button
              onClick={currentStep === 1 ? handleCancel : handleBack}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {currentStep === 1 ? 'Cancel' : (
                <>
                  <ChevronLeftIcon className="h-4 w-4 inline mr-1" />
                  Back
                </>
              )}
            </button>

            {currentStep < 4 ? (
              <button
                onClick={handleNext}
                disabled={!validateStep(currentStep)}
                className={`px-6 py-2 text-sm font-medium rounded-lg transition-colors ${
                  validateStep(currentStep)
                    ? 'text-white bg-blue-600 hover:bg-blue-700'
                    : 'text-gray-400 bg-gray-100 cursor-not-allowed'
                }`}
              >
                Next
                <ChevronRightIcon className="h-4 w-4 inline ml-1" />
              </button>
            ) : (
              <button
                onClick={handleCreate}
                disabled={submitting}
                className="px-6 py-2 text-sm font-medium text-white rounded-lg transition-all"
                style={{
                  background: submitting
                    ? '#9CA3AF'
                    : `linear-gradient(135deg, ${colors.brand.primary} 0%, ${colors.brand.accent} 100%)`,
                  boxShadow: submitting ? 'none' : `0 4px 12px -2px ${colors.brand.primary}40`,
                }}
              >
                {submitting ? 'Creating...' : 'Create Engagement'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Add Party Modal */}
      <AddPartyModal
        isOpen={showAddPartyModal}
        onClose={() => setShowAddPartyModal(false)}
        onSuccess={handleClientCreated}
      />
    </div>
  );
};

export default CreateEngagementWizard;
